import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Alert, Divider, Typography,
} from '@mui/material';
import { Country, CountrySettings } from '../types/countryTypes';
import {
  createCountry, updateCountry, getCountrySettings, updateCountrySettings,
} from '../api/countryApi';

interface CountryFormProps {
  open: boolean;
  country: Country | null; // null = crear
  onClose: () => void;
  onSaved: () => void;
}

const defaultSettings: CountrySettings = {
  currency: '',
  workdayHours: 8,
  decimalSeparator: '.',
  overtimeEnabled: false,
  subcontractEnabled: false,
  rosterEnabled: false,
};

const CountryForm: React.FC<CountryFormProps> = ({ open, country, onClose, onSaved }) => {
  const isEdit = Boolean(country);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState(true);
  const [settings, setSettings] = useState<CountrySettings>(defaultSettings);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    if (country) {
      setCode(country.code || '');
      setName(country.name || '');
      setStatus(country.status !== false);
      // Cargar la configuración existente del país.
      getCountrySettings(country.id)
        .then((s) => setSettings({ ...defaultSettings, ...s }))
        .catch(() => setSettings(defaultSettings));
    } else {
      setCode('');
      setName('');
      setStatus(true);
      setSettings(defaultSettings);
    }
  }, [open, country]);

  const handleSave = async () => {
    if (!code.trim() || !name.trim()) {
      setError('El código y el nombre del país son obligatorios.');
      return;
    }
    if (!settings.currency.trim()) {
      setError('La moneda es obligatoria.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let countryId: number;
      if (isEdit && country) {
        await updateCountry(country.id, { code, name, status });
        countryId = country.id;
      } else {
        const created = await createCountry({ code, name, status });
        countryId = created.id;
      }
      await updateCountrySettings(countryId, settings);
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al guardar el país.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Editar país' : 'Nuevo país'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Código"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              inputProps={{ maxLength: 3 }}
              fullWidth
              placeholder="CL"
            />
          </Grid>
          <Grid item xs={8}>
            <TextField label="Nombre" value={name} onChange={(e) => setName(e.target.value)} fullWidth />
          </Grid>
          {isEdit && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={status} onChange={(e) => setStatus(e.target.checked)} />}
                label={status ? 'Activo' : 'Inactivo'}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Divider textAlign="left">
              <Typography variant="body2" color="text.secondary">
                Configuración por defecto del país
              </Typography>
            </Divider>
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info">
              Estos valores aplican por defecto a <strong>todos los proyectos de este país</strong>.
              Cada proyecto puede sobrescribir los permitidos desde su pestaña
              "País y configuración".
            </Alert>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Moneda"
              value={settings.currency}
              onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
              fullWidth
              placeholder="CLP"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Horas de jornada"
              type="number"
              value={settings.workdayHours}
              onChange={(e) => setSettings({ ...settings, workdayHours: Number(e.target.value) })}
              fullWidth
              inputProps={{ step: 0.5, min: 0 }}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Separador decimal</InputLabel>
              <Select
                label="Separador decimal"
                value={settings.decimalSeparator}
                onChange={(e) => setSettings({ ...settings, decimalSeparator: String(e.target.value) })}
              >
                <MenuItem value=".">Punto ( . )</MenuItem>
                <MenuItem value=",">Coma ( , )</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Switch checked={settings.overtimeEnabled}
                onChange={(e) => setSettings({ ...settings, overtimeEnabled: e.target.checked })} />}
              label="Horas extras"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Switch checked={settings.subcontractEnabled}
                onChange={(e) => setSettings({ ...settings, subcontractEnabled: e.target.checked })} />}
              label="Subcontratas"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Switch checked={settings.rosterEnabled}
                onChange={(e) => setSettings({ ...settings, rosterEnabled: e.target.checked })} />}
              label="Cuadrante de turnos"
            />
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CountryForm;
